import { syncSuperProps, unregisterSuper } from "@/analytics/core";
import { useCanvasById } from "@/api/canvas";
import { ROUTES } from "@/constants/routes";
import { useOpenTabs } from "@/context/OpenTabsContext";
import { useSidebarContext } from "@/context/SidebarContext";
import { useDisplayMode } from "@/hooks/useAppBadge";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useEffect } from "react";
import { useMatch } from "react-router-dom";

const VOLATILE_KEYS = [
  "workspace_id",
  "canvas_id",
  "active_rail",
  "rail_open",
  "open_tab_count",
  "display_mode",
  "viewport",
];

/**
 * Volatile super properties describing the shell the user is looking at.
 * Registered { persistent: false } so a stale workspace_id/canvas_id can
 * never leak into the next session via localStorage; unregistered on
 * unmount. Mounted in WorkspaceLayout next to <WorkspaceTabBarSync/>.
 */
export function AnalyticsShellContextSync() {
  const workspaceMatch = useMatch(ROUTES.WORKSPACES.DETAIL);
  const canvasMatch = useMatch(ROUTES.CANVAS.DETAIL);
  const canvasId = canvasMatch?.params.canvasId;
  const { data: canvas } = useCanvasById(canvasId);
  const { activeRail, railOpen } = useSidebarContext();
  const { openTabs } = useOpenTabs();
  const displayMode = useDisplayMode();
  const lg = useBreakpoint("lg");

  const workspaceId =
    workspaceMatch?.params.workspaceId ?? canvas?.workspace_id;

  useEffect(() => {
    // Leaving a canvas must clear canvas_id/workspace_id — syncSuperProps
    // unregisters the keys whose value went away.
    syncSuperProps(
      {
        workspace_id: workspaceId,
        canvas_id: canvasId,
        active_rail: activeRail,
        rail_open: railOpen,
        open_tab_count: openTabs.length,
        display_mode: displayMode,
        viewport: lg ? "desktop" : "compact",
      },
      { persistent: false },
    );
  }, [
    workspaceId,
    canvasId,
    activeRail,
    railOpen,
    openTabs.length,
    displayMode,
    lg,
  ]);

  useEffect(() => () => unregisterSuper(VOLATILE_KEYS), []);

  return null;
}
